"use client"

import { Head, Link, router } from "@inertiajs/react"
import {
  Edit02,
  MusicNote01,
  PlayCircle,
  Plus,
  Share03,
} from "@untitledui/icons"
import { useState } from "react"

import { ShareLinkChip } from "@/components/collabs"
import { TextHTML, TrackCard } from "@/components/playlists"
import { Avatar } from "@/components/ui/avatar"
import { Breadcrumbs } from "@/components/ui/breadcrumbs"
import { Button } from "@/components/ui/button"
import { DestructiveStackedLeftAlignedModal } from "@/components/ui/destructive-stacked-left-aligned-modal"
import { FeaturedIcon } from "@/components/ui/featured-icon"
import { PaginationPageMinimalCenter } from "@/components/ui/pagination"
import { TagBadgeGroup } from "@/components/ui/tag-badge"
import { AuthedShell } from "@/layouts/authed-shell"
import { getPaginationUrl } from "@/lib/navigations"
import { formatDate, formatPaginationOrder } from "@/lib/utils/"
import { playlistPath, profilePath } from "@/routes"
import type { Playlist, Track } from "@/types"
import type { Pagination } from "@/types/contents/pagination"

interface PlaylistShowProps {
  playlist: Playlist
  canEdit: boolean
  ownedPlaylists: Playlist[]
  pagination: Pagination
}

export default function PlaylistShow(props: PlaylistShowProps) {
  const { playlist, canEdit, ownedPlaylists, pagination } = props

  const [trackToDelete, setTrackToDelete] = useState<Track | null>(null)
  // Local drag-reorder state that resets when the upstream playlist prop
  // changes. We pair a value with the playlist identity instead of syncing
  // via useEffect (which would cascade renders / fail set-state-in-effect).
  const [localOrder, setLocalOrder] = useState<{
    key: number
    tracks: Track[]
  } | null>(null)
  const orderedTracks =
    localOrder?.key === playlist.id ? localOrder.tracks : playlist.tracks
  const [draggingTrackId, setDraggingTrackId] = useState<
    number | string | null
  >(null)

  const handleShowTrack = (track: Track) => {
    router.visit(
      `/playlists/${playlist.slug ?? playlist.id}/tracks/${track.id}`,
    )
  }

  const handleEditTrack = (track: Track) => {
    router.visit(
      `/playlists/${playlist.slug ?? playlist.id}/tracks/${track.id}/edit`,
    )
  }

  const handleDeleteTrack = (track: Track) => {
    setTrackToDelete(track)
  }

  const handleConfirmDelete = () => {
    if (!trackToDelete) return
    router.delete(
      `/playlists/${playlist.slug ?? playlist.id}/tracks/${trackToDelete.id}`,
    )
    setTrackToDelete(null)
  }

  // The Play button opens the immersive route; clicking a thumbnail jumps
  // straight to that track's position via the `?t=…` query param. Previously
  // these branched into a modal player ref; the route-based shell is the
  // surface now.
  const playPath = `/playlists/${playlist.slug ?? playlist.id}/play`
  const handleOpenImmersivePlayer = () => {
    router.visit(playPath)
  }
  const handleOpenImmersivePlayerAt = (position: number) => {
    router.visit(`${playPath}?t=${position}`)
  }

  const handleDragStartTrack = (track: Track) => {
    setDraggingTrackId(track.id)
  }

  const handleDragOverTrack = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const handleDropTrack = (targetTrack: Track) => {
    if (!draggingTrackId || draggingTrackId === targetTrack.id) return
    const fromIndex = orderedTracks.findIndex((t) => t.id === draggingTrackId)
    const toIndex = orderedTracks.findIndex((t) => t.id === targetTrack.id)
    if (fromIndex === -1 || toIndex === -1) return

    const updated = [...orderedTracks]
    const [moved] = updated.splice(fromIndex, 1)
    updated.splice(toIndex, 0, moved)
    setLocalOrder({ key: playlist.id, tracks: updated })
    setDraggingTrackId(null)

    const playlistPath = `/playlists/${playlist.slug ?? playlist.id}`
    router.patch(`${playlistPath}/tracks/${draggingTrackId}/move`, {
      position: toIndex + 1,
    })
  }

  const trackCount = playlist.tracks_count ?? orderedTracks.length

  return (
    <AuthedShell headerVariant="light">
      <Head title={playlist.title} />

      {/* Editorial hero — record-sleeve layout on a paper-grey band, sitting
          beside briefs#show in weight. */}
      <section className="bg-secondary pt-6 pb-12 lg:pb-16">
        <div className="max-w-container mx-auto px-4 lg:px-8">
          <div className="mb-8">
            <Breadcrumbs maxVisibleItems={3} />
          </div>

          <div className="flex flex-col gap-8 md:flex-row md:gap-10">
            {/* Cover */}
            <div className="bg-secondary_subtle aspect-square w-full shrink-0 overflow-hidden rounded-2xl shadow-xs md:w-56 lg:w-64">
              {playlist.cover_image_url ? (
                <img
                  src={playlist.cover_image_url}
                  alt={playlist.title}
                  className="size-full object-cover"
                />
              ) : (
                <div className="bg-brand-50 flex size-full items-center justify-center">
                  <MusicNote01
                    aria-hidden="true"
                    className="text-brand-300 size-16"
                  />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex flex-1 flex-col gap-4">
              <p className="text-tertiary text-xs font-medium tracking-wider uppercase">
                Playlist
              </p>

              <h1 className="text-display-sm text-primary lg:text-display-md font-semibold text-balance">
                {playlist.title}
              </h1>

              {/* Byline + metadata */}
              <div className="text-tertiary flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                {playlist.user && (
                  <div className="flex items-center gap-2">
                    <Avatar
                      name={playlist.user.name ?? ""}
                      avatarUrl={playlist.user.avatar_url as string}
                      size="xs"
                    />
                    {playlist.user.slug ? (
                      <Link
                        href={profilePath(playlist.user.slug)}
                        className="text-primary inline-flex items-center gap-1 text-sm font-medium"
                      >
                        <span>{playlist.user.name}</span>
                        <Share03
                          aria-hidden="true"
                          className="size-3 shrink-0"
                        />
                      </Link>
                    ) : (
                      <span className="text-secondary font-medium">
                        {playlist.user.name}
                      </span>
                    )}
                  </div>
                )}
                <span>
                  {trackCount} {trackCount === 1 ? "track" : "tracks"}
                </span>
                <span>Created {formatDate(playlist.created_at)}</span>
              </div>

              {/* Description */}
              {playlist.description && (
                <div className="max-w-2xl">
                  <TextHTML text={playlist.description} />
                </div>
              )}

              {/* Tags — synced onto the playlist from its posts' tags */}
              <TagBadgeGroup tags={playlist.tags} />

              {/* Actions */}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <Button
                  size="md"
                  iconLeading={<PlayCircle className="text-secondary size-6" />}
                  onClick={handleOpenImmersivePlayer}
                  className="w-full sm:w-auto"
                  isDisabled={!playlist.has_playable_tracks}
                >
                  Play
                </Button>
                {canEdit && (
                  <Button
                    size="md"
                    color="secondary"
                    iconLeading={Edit02}
                    href={`/playlists/${playlist.slug ?? playlist.id}/edit`}
                    className="w-full sm:w-auto"
                  >
                    Edit info
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tracks run in a constrained column beside an "About this list"
          companion aside — the tactile treatment the briefs redesign earned.
          Below `lg` the aside folds beneath the list (source order: list
          first). This region is layout-neutral: the shell provides the
          single `<main>`. */}
      <div className="max-w-container mx-auto w-full px-4 py-10 lg:px-8 lg:py-12">
        <div className="grid grid-cols-1 gap-x-10 gap-y-10 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-y-0">
          {/* Tracks column */}
          <div className="flex min-w-0 flex-col gap-6">
            {/* Tracks section header */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <h2 className="text-primary text-xl font-semibold">Tracks</h2>
                {orderedTracks.length > 0 && (
                  <p className="text-tertiary text-sm">
                    {trackCount} {trackCount === 1 ? "track" : "tracks"} in this
                    playlist
                  </p>
                )}
              </div>
              {canEdit && (
                <Button
                  size="sm"
                  color="secondary"
                  iconLeading={Plus}
                  href={`/playlists/${playlist.slug ?? playlist.id}/tracks/new`}
                >
                  New track
                </Button>
              )}
            </div>

            {orderedTracks.length > 0 ? (
              <div className="flex flex-col gap-3">
                {orderedTracks.map((track, index) => (
                  <TrackCard
                    key={track.id}
                    track={track}
                    playlist={playlist}
                    position={formatPaginationOrder(pagination.page, index)}
                    showActions={canEdit}
                    onEdit={handleEditTrack}
                    onDelete={handleDeleteTrack}
                    onClickTitle={handleShowTrack}
                    onClickThumbnail={handleOpenImmersivePlayerAt}
                    draggable={canEdit}
                    onDragStart={(_event, t) => handleDragStartTrack(t)}
                    onDragOver={handleDragOverTrack}
                    onDrop={(_event, t) => handleDropTrack(t)}
                    playlistRepository={ownedPlaylists.filter(
                      (owned) => owned.id !== playlist.id,
                    )}
                  />
                ))}
                {pagination && pagination.total > 1 && (
                  <PaginationPageMinimalCenter
                    page={pagination.page}
                    total={pagination.total}
                    className="mt-6"
                    onPageChange={(page) =>
                      router.visit(
                        getPaginationUrl(
                          playlistPath(playlist.slug ?? playlist.id),
                          page,
                        ),
                      )
                    }
                  />
                )}
              </div>
            ) : (
              <div className="mx-auto flex w-full max-w-md flex-col items-center justify-center py-16">
                <FeaturedIcon
                  icon={MusicNote01}
                  color="gray"
                  theme="modern"
                  size="lg"
                />
                <div className="mt-4 mb-6 flex flex-col items-center gap-1 text-center">
                  <h3 className="text-md text-primary font-semibold">
                    No tracks yet
                  </h3>
                  <p className="text-tertiary text-sm">
                    {canEdit
                      ? "Add a new track to get started."
                      : "This playlist doesn't have any tracks yet."}
                  </p>
                </div>
                {canEdit && (
                  <Button
                    href={`/playlists/${playlist.slug ?? playlist.id}/tracks/new`}
                    iconLeading={Plus}
                    size="sm"
                  >
                    New track
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Companion aside — "About this list" + share chip. Sticky on
              desktop so it stays with the reader as the tracks scroll. */}
          <aside className="flex flex-col gap-5 lg:sticky lg:top-4 lg:self-start">
            <AboutThisList playlist={playlist} trackCount={trackCount} />
            {playlist.canonical_url && (
              <ShareLinkChip
                url={playlist.canonical_url}
                heading="Share this list"
                caption="Pass it to someone whose taste belongs in here."
              />
            )}
          </aside>
        </div>
      </div>

      <DestructiveStackedLeftAlignedModal
        isOpen={!!trackToDelete}
        onOpenChange={(open) => !open && setTrackToDelete(null)}
        title={`Delete "${trackToDelete?.post?.title ?? ""}"?`}
        description="Are you sure you want to remove this track from the playlist? This action cannot be undone."
        onConfirm={handleConfirmDelete}
      />
    </AuthedShell>
  )
}

/**
 * The companion aside's masthead card — the list's curator + metadata + tags,
 * echoing the hero so the reader keeps its context beside the scrolling
 * tracks. Grammar matches the briefs `ContributorsSidebar` panel.
 */
function AboutThisList({
  playlist,
  trackCount,
}: {
  playlist: Playlist
  trackCount: number
}) {
  return (
    <div className="border-secondary bg-primary flex flex-col gap-5 rounded-xl border p-5">
      <h2 className="text-tertiary text-xs font-semibold tracking-wide uppercase">
        About this list
      </h2>

      {playlist.user && (
        <div className="flex items-center gap-3">
          <Avatar
            name={playlist.user.name ?? ""}
            avatarUrl={playlist.user.avatar_url as string}
            size="sm"
          />
          <div className="flex flex-col">
            {playlist.user.slug ? (
              <Link
                href={profilePath(playlist.user.slug)}
                className="text-primary inline-flex items-center gap-1 text-sm font-medium"
              >
                <span>{playlist.user.name}</span>
                <Share03 aria-hidden="true" className="size-3 shrink-0" />
              </Link>
            ) : (
              <span className="text-primary text-sm font-medium">
                {playlist.user.name}
              </span>
            )}
            <span className="text-tertiary text-xs">Curator</span>
          </div>
        </div>
      )}

      <dl className="flex flex-col gap-2 text-sm">
        <div className="flex items-center justify-between gap-4">
          <dt className="text-tertiary">Tracks</dt>
          <dd className="text-secondary font-medium">{trackCount}</dd>
        </div>
        <div className="flex items-center justify-between gap-4">
          <dt className="text-tertiary">Created</dt>
          <dd className="text-secondary font-medium">
            {formatDate(playlist.created_at)}
          </dd>
        </div>
      </dl>

      {playlist.tags.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-tertiary text-xs font-semibold tracking-wide uppercase">
            Tags
          </h3>
          <TagBadgeGroup tags={playlist.tags} />
        </div>
      )}
    </div>
  )
}
